import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap, finalize } from 'rxjs/operators';
import { ProductService } from '@core';
import { usdToPoints } from '@models';
import type { ProductDetails, ProductDetailsWithVariant, VariantGrouping } from '@models';
import type { RadioOption } from '../../../../ui/apple-components/radio-button-group/radio-button-group.component';
import type { RadioColorOption } from '../../../../ui/apple-components/radio-color/radio-color.component';

export interface VariantRadioGroup {
  title: string;
  options: RadioOption[];
  name: string;
  selectedValue: string;
  order: number;
}

export interface ProductVarianceResult {
  variantRadioGroups: VariantRadioGroup[];
  variantColorOptions: RadioColorOption[];
  selectedColorValue: string;
  productVariances: any[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductVarianceService {
  private productService = inject(ProductService);

  // Cache for groupings to avoid repeated processing
  private groupingsCache = new Map<string, VariantGrouping[]>();

  fetchProductVariances(product: ProductDetailsWithVariant): Observable<ProductVarianceResult> {
    const selectedOptionMap = new Map<string, string>();

    // Step 1: Group by partNumber (consistent for all products now)
    const matchingGroupings = this.getGroupingsByPartNumber(product);

    // Step 2: Find current product's grouping and populate selected options

    const currentGrouping = this.findCurrentProductGrouping(product, matchingGroupings);

    if (currentGrouping) {
      this.populateSelectedOptionMapFromGrouping(currentGrouping, selectedOptionMap);
    }

    return this.productService.getProductVariances(product.productId.toString()).pipe(
      map((variances) => {
        // Step 3: Filter variances based on valid options

        const validOptionValuesByVarianceName = this.buildValidOptionValuesFromGroupings(matchingGroupings, variances);

        const filteredVariances = this.filterVariancesByValidOptions(variances, validOptionValuesByVarianceName);

        // Step 4: Build UI components

        const result = this.buildVariantUIFromGroupings(filteredVariances, selectedOptionMap, matchingGroupings);

        return {
          ...result,
          productVariances: filteredVariances,
        };
      }),
    );
  }

  /**
   * Groups products by partNumber pattern - this is now the standard approach for all products
   * Optimized with caching to avoid repeated processing
   */
  getGroupingsByPartNumber(product: ProductDetailsWithVariant): VariantGrouping[] {
    const cacheKey = `${product.productId}_${product.variantGroupings?.[0]?.partNumber}`;

    if (this.groupingsCache.has(cacheKey)) {
      return this.groupingsCache.get(cacheKey)!;
    }

    const allGroupings: VariantGrouping[] = [
      ...(product.variantGroupings ?? []),
      ...(product.similarProducts?.flatMap((p) => p.variantGroupings ?? []) ?? []),
    ];

    // Find the current product's base part number
    const currentGrouping = product.variantGroupings?.find((g) => g.parentProductId === product.productId);

    if (!currentGrouping) {
      return [];
    }

    const basePartNumber = currentGrouping.partNumber;

    // Find all groupings that share the same base part number
    const relatedGroupings = allGroupings.filter((grouping) => {
      return grouping.partNumber === basePartNumber;
    });

    // Cache the result
    this.groupingsCache.set(cacheKey, relatedGroupings);

    return relatedGroupings;
  }

  /**
   * Builds valid option values by including ALL productVariance options that have potential matches
   * The key insight: Don't filter by current groupings, use them for availability logic instead
   */
  private buildValidOptionValuesFromGroupings(
    groupings: VariantGrouping[],
    productVariances: any[],
  ): Map<string, Set<string>> {
    const validOptionValuesByVarianceName = new Map<string, Set<string>>();

    // Step 1: Get all variance values that exist across the product family groupings
    const availableVarianceValues = new Map<string, Set<string>>();

    for (const grouping of groupings) {
      for (const option of grouping.options ?? []) {
        const varianceName = option.variance?.name;
        const optionValue = option.option?.value;

        if (varianceName && optionValue) {
          if (!availableVarianceValues.has(varianceName)) {
            availableVarianceValues.set(varianceName, new Set());
          }
          availableVarianceValues.get(varianceName)!.add(optionValue);
        }
      }
    }

    // Step 2: Include ALL productVariance options that have matching values in the product family
    for (const variance of productVariances) {
      const varianceName = variance.name;
      if (!varianceName) continue;

      if (!validOptionValuesByVarianceName.has(varianceName)) {
        validOptionValuesByVarianceName.set(varianceName, new Set());
      }

      const availableValues = availableVarianceValues.get(varianceName) || new Set();

      for (const option of variance.options ?? []) {
        if (!option.id) continue;

        // Try exact match first
        let isIncluded = availableValues.has(option.value);

        // If no exact match, try normalized matching
        if (!isIncluded) {
          const normalizedOptionValue = this.normalizeValue(option.value);

          for (const availableValue of availableValues) {
            if (normalizedOptionValue === this.normalizeValue(availableValue)) {
              isIncluded = true;
              break;
            }
          }
        }

        // If still no match, try semantic matching (handles hyphens, special chars)
        if (!isIncluded) {
          isIncluded = this.isSemanticMatch(option.value, availableValues, varianceName);
        }

        if (isIncluded) {
          validOptionValuesByVarianceName.get(varianceName)!.add(option.id);
        }
      }
    }

    return validOptionValuesByVarianceName;
  }
  /**
   * Finds the current product's grouping from the matching groupings
   */
  private findCurrentProductGrouping(
    product: ProductDetailsWithVariant,
    matchingGroupings: VariantGrouping[],
  ): VariantGrouping | undefined {
    return matchingGroupings.find((g) => g.parentProductId === product.productId);
  }

  /**
   * Populates the selected option map from the current grouping using option values
   */
  private populateSelectedOptionMapFromGrouping(
    currentGrouping: VariantGrouping,
    selectedOptionMap: Map<string, string>,
  ): void {
    for (const option of currentGrouping.options ?? []) {
      const name = option.variance?.name?.trim();
      const value = option.option?.value; // Use option value for smart matching
      if (name && value) {
        selectedOptionMap.set(name, value);
      }
    }
  }

  /**
   * Filters product variances based on valid option IDs
   */
  private filterVariancesByValidOptions(
    variances: any[],
    validOptionValuesByVarianceName: Map<string, Set<string>>,
  ): any[] {
    return variances
      .map((variance) => {
        const name = variance.name ?? '';
        const allowedOptionIds = validOptionValuesByVarianceName.get(name);

        const filteredOptions =
          allowedOptionIds && allowedOptionIds.size > 0 && variance.options
            ? variance.options.filter((opt) => allowedOptionIds.has(opt.id))
            : [];

        return {
          ...variance,
          options: filteredOptions,
        };
      })
      .filter((v) => v.options && v.options.length > 0);
  }

  private buildVariantUIFromGroupings(
    filteredVariances: any[],
    selectedOptionMap: Map<string, string>,
    matchingGroupings: VariantGrouping[],
  ): Omit<ProductVarianceResult, 'productVariances'> {
    const radioGroups: VariantRadioGroup[] = [];
    const colorOptions: RadioColorOption[] = [];
    let selectedColorValue = '';

    // Check if this is an Apple Watch product by examining the groupings
    const isAppleWatch = this.isAppleWatchFromGroupings(matchingGroupings);

    for (const variance of filteredVariances) {
      const selectedOptionValue = selectedOptionMap.get(variance.name ?? '');

      // Get available option IDs based on current selections
      const availableOptionIds = this.getAvailableOptionsForVarianceFromGroupings(
        variance.name ?? '',
        selectedOptionMap,
        matchingGroupings,
      );

      if (variance.controlType === 'Dropdown') {
        // SKIP color dropdowns for Apple Watch products
        if (isAppleWatch && variance.name?.toLowerCase() === 'color') {
          continue;
        }

        // Process normal dropdowns (including non-color dropdowns for Apple Watch)
        const dropdownOptions = (variance.options ?? []).map((opt) => {
          const additionalPrice = opt.additionalPrice;
          // Normalize values to ensure consistency across products
          const varianceLower = variance.name?.toLowerCase() || '';
          const needsNormalization =
            varianceLower.includes('communication') ||
            varianceLower.includes('model') ||
            varianceLower.includes('size') ||
            varianceLower.includes('screen');
          const normalizedValue = needsNormalization
            ? this.normalizeVarianceValue(opt.value, variance.name || '')
            : opt.value;

          return {
            id: opt.id,
            name: opt.displayValue,
            value: normalizedValue,
            price: additionalPrice,
            disabled: !availableOptionIds.has(opt.id),
            points: additionalPrice ? usdToPoints(additionalPrice) : undefined,
            order: opt.order,
          };
        });

        // Find the selected option ID from the original variance options (using semantic matching)
        const selectedOption = variance.options.find(
          (opt) =>
            opt.value === selectedOptionValue ||
            this.areValuesSemanticallySame(opt.value, selectedOptionValue || '', variance.name || ''),
        );
        const selectedId = selectedOption?.id ?? '';

        // Remove duplicates by value, preserving selected option
        const deduplicatedOptions = this.removeDuplicatesByValue(dropdownOptions, variance.name ?? '', selectedId);

        // Sort by order property, but use intelligent numerical sorting for storage/memory
        deduplicatedOptions.sort((a, b) => {
          const varianceType = variance.name?.toLowerCase() || '';

          // For storage and memory, sort by numerical value instead of order property
          if (varianceType.includes('storage') || varianceType.includes('memory')) {
            const numericA = this.extractNumericValue(a.value);
            const numericB = this.extractNumericValue(b.value);

            if (numericA !== numericB) {
              return numericA - numericB;
            }
          }

          // Fall back to order property for other variances or when numeric values are equal
          const orderA = a.order;
          const orderB = b.order;
          return orderA - orderB;
        });

        radioGroups.push({
          title: variance.name,
          name: variance.name || `variant-${variance.id}`,
          options: deduplicatedOptions,
          selectedValue: selectedId,
          order: variance.order,
        });
      }

      if (variance.controlType === 'ColorTiles') {
        // Process color tiles normally (for non-Apple Watch products)
        const colorOptionsForVariance: RadioColorOption[] = [];

        for (const option of variance.options) {
          if (option.colorCode) {
            colorOptionsForVariance.push({
              id: option.id,
              color: option.colorCode,
              label: option.displayValue,
              value: option.value,
            });

            if (
              selectedOptionValue === option.value ||
              this.areValuesSemanticallySame(option.value, selectedOptionValue || '', 'color')
            ) {
              selectedColorValue = option.value;
            }
          }
        }

        const deduplicatedColors = this.removeDuplicateColorsByValueWithOrder(colorOptionsForVariance);
        colorOptions.push(...deduplicatedColors);
      }
    }

    // Sort radio groups once at the end
    radioGroups.sort((a, b) => (a as any).order - (b as any).order);

    return {
      variantRadioGroups: radioGroups,
      variantColorOptions: colorOptions,
      selectedColorValue,
    };
  }

  // Add this helper method to detect Apple Watch from groupings:
  private isAppleWatchFromGroupings(matchingGroupings: VariantGrouping[]): boolean {
    const hasWatchVariances = matchingGroupings.some((grouping) =>
      grouping.options?.some((option) => {
        const varianceName = option.variance?.name?.toLowerCase();
        return (
          varianceName?.includes('case') ||
          varianceName?.includes('band') ||
          (varianceName?.includes('size') && varianceName?.includes('mm'))
        );
      }),
    );

    // Method 2: Check for Apple Watch specific option values
    const hasWatchSizes = matchingGroupings.some((grouping) =>
      grouping.options?.some((option) => {
        const value = option.option?.value?.toLowerCase();
        return value?.includes('mm') || value?.includes('ultra') || value?.includes('series');
      }),
    );

    return hasWatchVariances || hasWatchSizes;
  }

  /**
   * Remove duplicate colors while preserving the original order from variance.options
   * This ensures color order stays consistent regardless of which color is selected
   */
  private removeDuplicateColorsByValueWithOrder(colorOptions: RadioColorOption[]): RadioColorOption[] {
    const seen = new Map<string, RadioColorOption>();

    for (const option of colorOptions) {
      const normalizedValue = this.normalizeValue(option.value);

      if (!seen.has(normalizedValue)) {
        seen.set(normalizedValue, option);
      }
    }

    const uniqueColors = Array.from(seen.values());

    uniqueColors.sort((a, b) => a.value.localeCompare(b.value));

    return uniqueColors;
  }

  private removeDuplicatesByValue(options: any[], varianceName: string, selectedValue?: string): any[] {
    const seen = new Map<string, any>();
    const result: any[] = [];

    for (const option of options) {
      const value = option.value;
      const normalizedValue = this.normalizeForDeduplication(value, varianceName);

      if (!seen.has(normalizedValue)) {
        seen.set(normalizedValue, option);
        result.push(option);
      } else {
        const existing = seen.get(normalizedValue);
        const existingIndex = result.findIndex((opt) => opt.id === existing.id);

        // CRITICAL: If the new option is the selected one, always keep it in the same position
        if (selectedValue && option.id === selectedValue) {
          if (existingIndex !== -1) {
            result[existingIndex] = option; // Replace in same position
            seen.set(normalizedValue, option);
          }
          continue;
        }

        // CRITICAL: If the existing option is the selected one, never replace it
        if (selectedValue && existing.id === selectedValue) {
          continue; // Keep the existing selected option in its position
        }

        // Apply preference logic but maintain position
        const shouldReplace = this.shouldReplaceExisting(existing, option, varianceName);

        if (shouldReplace && existingIndex !== -1) {
          // Replace existing with preferred version BUT keep the same array position
          result[existingIndex] = option;
          seen.set(normalizedValue, option);
        }
      }
    }

    return result;
  }

  private normalizeForDeduplication(value: string, varianceName: string): string {
    if (!value) return value;

    let normalized = value.trim().toLowerCase();

    if (varianceName?.toLowerCase().includes('storage') || normalized.includes('gb') || normalized.includes('tb')) {
      normalized = normalized.replace(/(\d+)\s*(gb|tb)/gi, '$1$2');
    }

    if (varianceName?.toLowerCase().includes('memory')) {
      normalized = normalized.replace(/(\d+)\s*(gb)/gi, '$1$2');
    }

    return normalized;
  }

  private shouldReplaceExisting(existing: any, newOption: any, varianceName: string): boolean {
    // Always prefer enabled over disabled
    if (existing.disabled && !newOption.disabled) {
      return true;
    }
    if (!existing.disabled && newOption.disabled) {
      return false;
    }

    // If both have same enabled state, apply formatting preferences
    const existingValue = existing.value;
    const newValue = newOption.value;

    // For storage: prefer format WITH spaces
    if (varianceName?.toLowerCase().includes('storage')) {
      if (!existingValue.includes(' ') && newValue.includes(' ')) {
        return true;
      }
      if (existingValue.includes(' ') && !newValue.includes(' ')) {
        return false;
      }
    }

    // For memory: prefer format WITH spaces
    if (varianceName?.toLowerCase().includes('memory')) {
      if (!existingValue.includes(' ') && newValue.includes(' ')) {
        return true;
      }
      if (existingValue.includes(' ') && !newValue.includes(' ')) {
        return false;
      }
    }

    // Default: prefer longer value (spaced format is usually longer)
    return newValue.length > existingValue.length;
  }

  private extractNumericValue(value: string): number {
    if (!value) return 0;

    const normalized = value.toLowerCase().trim();

    // Extract number and unit
    const match = normalized.match(/(\d+(?:\.\d+)?)\s*(gb|tb)/);
    if (!match) return 0;

    const number = parseFloat(match[1]);
    const unit = match[2];

    // Convert TB to GB for consistent comparison (1TB = 1024GB)
    if (unit === 'tb') {
      return number * 1024;
    }

    return number;
  }

  private getAvailableOptionsForVarianceFromGroupings(
    targetVariance: string,
    selectedOptionMap: Map<string, string>,
    allGroupings: VariantGrouping[],
  ): Set<string> {
    const result = new Set<string>();

    for (const grouping of allGroupings) {
      const groupingOptions = grouping.options ?? [];

      // Check if this grouping matches all currently selected options (except the target variance)
      const matches = groupingOptions.every((opt) => {
        const name = opt.variance?.name;
        const value = opt.option?.value;

        if (!name || !value) return false;
        if (name === targetVariance) return true; // skipping most recently selected varaince in matching

        const selectedValue = selectedOptionMap.get(name);
        return !selectedValue || selectedValue === value;
      });

      if (matches) {
        // Add all options for the target variance from this grouping
        for (const opt of groupingOptions) {
          if (opt.variance?.name === targetVariance && opt.option?.id) {
            result.add(opt.option.id);
          }
        }
      }
    }

    return result;
  }

  findSmartMatchingProduct(
    selectedOptionMap: Map<string, string>,
    product: ProductDetailsWithVariant,
    newlyChanged: { varianceName: string; value: string },
  ): ProductDetailsWithVariant | undefined {
    // Get all groupings using the new partNumber-based approach
    const allGroupings = this.getGroupingsByPartNumber(product);

    if (allGroupings.length === 0) {
      return undefined;
    }

    // Find candidate groupings with enhanced matching (including semantic)
    const candidateGroupings = allGroupings.filter((grouping) => {
      return grouping.options.some((opt) => {
        const optVarianceName = opt.variance?.name?.trim().toLowerCase();
        const searchVarianceName = newlyChanged.varianceName.trim().toLowerCase();
        const optValue = opt.option?.value;

        if (optVarianceName !== searchVarianceName) {
          return false;
        }

        // Try exact match first
        if (optValue === newlyChanged.value) {
          return true;
        }

        // Try semantic matching
        if (this.areValuesSemanticallySame(newlyChanged.value, optValue, newlyChanged.varianceName)) {
          return true;
        }

        return false;
      });
    });

    if (candidateGroupings.length === 0) {
      return undefined;
    }

    // Score candidates with enhanced matching
    let bestMatch: VariantGrouping | undefined;
    let bestScore = -1;
    let bestPreservedCount = -1;

    for (const grouping of candidateGroupings) {
      let preservedCount = 0;
      let totalRelevantOptions = 0;

      // Count how many of the OTHER selected options this grouping preserves
      for (const [varianceName, selectedValue] of selectedOptionMap.entries()) {
        const normalizedVarianceName = varianceName.trim().toLowerCase();
        const normalizedChangedName = newlyChanged.varianceName.trim().toLowerCase();

        // Skip the option we're intentionally changing
        if (normalizedVarianceName === normalizedChangedName) {
          continue;
        }

        totalRelevantOptions++;

        // Enhanced matching for preserved options
        const matchedOption = grouping.options.find((opt) => {
          const optVarianceName = opt.variance?.name?.trim().toLowerCase();
          const optValue = opt.option?.value;

          if (optVarianceName !== normalizedVarianceName) {
            return false;
          }

          // Try exact match
          if (optValue === selectedValue) {
            return true;
          }

          // Try semantic matching for preserved options too
          return this.areValuesSemanticallySame(selectedValue, optValue, varianceName);
        });

        if (matchedOption) {
          preservedCount++;
        }
      }

      // Calculate score: prioritize preserving more of the existing selections
      const score = totalRelevantOptions > 0 ? (preservedCount / totalRelevantOptions) * 100 : 100;

      // Prefer candidates that preserve more options
      if (score > bestScore || (score === bestScore && preservedCount > bestPreservedCount)) {
        bestMatch = grouping;
        bestScore = score;
        bestPreservedCount = preservedCount;
      }
    }

    if (bestMatch) {
      // Don't switch if we're already on the best match
      if (bestMatch.parentProductId === product.productId) {
        return product;
      }

      // Find the actual product object
      const foundProduct = product.similarProducts?.find((similar) => similar.productId === bestMatch!.parentProductId);

      if (foundProduct) {
        return foundProduct;
      }
    }

    return undefined;
  }

  buildSelectedOptionMap(variantRadioGroups: VariantRadioGroup[]): Map<string, string> {
    const map = new Map<string, string>();

    for (const group of variantRadioGroups) {
      const selectedValue = group.selectedValue;
      const varianceName = group.title;

      if (selectedValue && varianceName) {
        map.set(varianceName, selectedValue);
      }
    }

    return map;
  }

  getAvailableOptionsForVariance(
    targetVariance: string,
    selectedOptionMap: Map<string, string>,
    allGroupings: VariantGrouping[],
  ): Set<string> {
    return this.getAvailableOptionsForVarianceFromGroupings(targetVariance, selectedOptionMap, allGroupings);
  }

  isProductWithVariants(product: ProductDetails): product is ProductDetailsWithVariant {
    return 'similarProducts' in product && Array.isArray((product as any).similarProducts);
  }

  findMatchingSimilarProduct(
    product: ProductDetailsWithVariant,
    variantRadioGroups: VariantRadioGroup[],
    variantColorOptions: RadioColorOption[],
    selectedColorValue: string,
    options: {
      updatedOptionId?: string;
      updatedOptionValue?: string;
    },
  ): ProductDetailsWithVariant | null {
    if (!this.isProductWithVariants(product)) {
      return null;
    }
    // IMPORTANT: Use filtered groupings instead of all similarProducts
    const filteredGroupings = this.getGroupingsByPartNumber(product);
    const allowedProductIds = new Set(filteredGroupings.map((g) => g.parentProductId));

    // Filter similarProducts to only include those in the allowed groupings
    const filteredSimilarProducts =
      product.similarProducts?.filter((similar) => allowedProductIds.has(similar.productId)) || [];

    const selectedValues = variantRadioGroups
      .map((group) => {
        const selected = group.options.find((opt) => opt.id === group.selectedValue);
        return selected?.value ?? null;
      })
      .filter(Boolean) as string[];

    const selectedColor = variantColorOptions.find((opt) => opt.value === selectedColorValue);
    if (selectedColor) {
      selectedValues.push(selectedColor.value);
    }

    if (options.updatedOptionValue && !selectedValues.includes(options.updatedOptionValue)) {
      selectedValues.push(options.updatedOptionValue);
    }

    const normalizedSelected = selectedValues.map((v) => this.normalizeValue(v)).sort();

    for (const similar of filteredSimilarProducts) {
      const grouping = similar.variantGroupings?.[0];
      if (!grouping) continue;

      const groupingValues = grouping.options
        .map((opt) => opt.option?.value)
        .filter(Boolean)
        .map((v) => this.normalizeValue(v))
        .sort();

      const isMatch =
        normalizedSelected.length === groupingValues.length &&
        normalizedSelected.every((val, i) => val === groupingValues[i]);

      if (isMatch) {
        return similar;
      }
    }

    return null;
  }

  findSimilarProductByColorOnly(
    currentProduct: ProductDetailsWithVariant,
    colorValue: string,
  ): ProductDetailsWithVariant | null {
    if (!currentProduct.similarProducts || currentProduct.similarProducts.length === 0) {
      return null;
    }

    return (
      currentProduct.similarProducts.find((similarProduct) => {
        // Check if this similar product has the exact color value we want
        const hasMatchingColor =
          similarProduct.productVariances?.some(
            (variance) => variance.name === 'Color' && variance.options?.some((option) => option.value === colorValue),
          ) ||
          similarProduct.variantGroupings?.some((grouping) =>
            grouping.options?.some(
              (option) => option.variance?.name === 'Color' && option.option?.value === colorValue,
            ),
          );

        return hasMatchingColor;
      }) || null
    );
  }

  private normalizeValue(val: string, stripSpecialChars = false): string {
    let result = val.trim().toLowerCase().replace(/\s+/g, '');
    if (stripSpecialChars) {
      result = result.replace(/[-+]/g, '');
    }
    return result;
  }
  public normalizeVarianceValue(value: string, varianceName: string): string {
    const lowerVariance = varianceName.toLowerCase();
    const lowerValue = value.toLowerCase();

    // Handle model/size/screen variations (same logic as semantic matching)
    if (lowerVariance.includes('model') || lowerVariance.includes('size') || lowerVariance.includes('screen')) {
      const sizeMappings = new Map([
        ['11inch', '11-inch'],
        ['11-inch', '11-inch'],
        ['13inch', '13-inch'],
        ['13-inch', '13-inch'],
        ['14inch', '14-inch'],
        ['14-inch', '14-inch'],
        ['15inch', '15-inch'],
        ['15-inch', '15-inch'],
        ['16inch', '16-inch'],
        ['16-inch', '16-inch'],
      ]);

      const normalizedKey = this.normalizeValue(lowerValue);
      const mappedValue = sizeMappings.get(normalizedKey);

      if (mappedValue) {
        return mappedValue;
      }
    }

    // Handle color abbreviation variations
    if (lowerVariance.includes('color')) {
      const colorMappings = new Map([
        ['sl', 'slv'], // SL -> SLV (Silver)
        ['slv', 'sl'], // SLV -> SL (reverse mapping)
        ['sb', 'spaceblack'], // SB -> Space Black
        ['spaceblack', 'sb'], // Space Black -> SB
        ['mdn', 'midnight'], // MDN -> Midnight
        ['midnight', 'mdn'], // Midnight -> MDN
        ['stl', 'starlight'], // STL -> Starlight
        ['starlight', 'stl'], // Starlight -> STL
      ]);

      const normalizedKey = this.normalizeValue(lowerValue);
      const mappedValue = colorMappings.get(normalizedKey);

      if (mappedValue) {
        return mappedValue;
      }
    }

    // Handle communication/connectivity variations
    if (lowerVariance.includes('communication') || lowerVariance.includes('connectivity')) {
      const communicationMappings = new Map([
        ['wificell', 'wi-fi + cellular'],
        ['wificellular', 'wi-fi + cellular'], // "wi-fi + cellular" with special chars stripped
        ['wifiandcellular', 'wi-fi + cellular'],
        ['cellular', 'wi-fi + cellular'],
        ['wifi', 'wi-fi'],
        ['wifionly', 'wi-fi'],
      ]);

      // Use stripSpecialChars=true to handle "Wi-Fi + Cellular" -> "wificellular"
      const normalizedKey = this.normalizeValue(lowerValue, true);
      const mappedValue = communicationMappings.get(normalizedKey);

      if (mappedValue) {
        return mappedValue;
      }
    }

    // If no mapping found, return original value
    return value;
  }

  /**
   * Check for semantic matches between option values (handles known variations)
   */
  /**
   * Check for semantic matches between option values (handles known variations)
   */
  private isSemanticMatch(optionValue: string, availableValues: Set<string>, varianceName: string): boolean {
    const lowerVariance = varianceName.toLowerCase();
    const lowerOption = optionValue.toLowerCase();

    // DEBUG: Add logging for color matching

    // Handle color abbreviation variations
    if (lowerVariance.includes('color')) {
      const colorMappings = new Map([
        // Common color abbreviations
        ['sl', 'slv'], // SL -> SLV (Silver)
        ['slv', 'sl'], // SLV -> SL (reverse mapping)
        ['sb', 'spaceblack'], // SB -> Space Black
        ['spaceblack', 'sb'], // Space Black -> SB
        ['mdn', 'midnight'], // MDN -> Midnight
        ['midnight', 'mdn'], // Midnight -> MDN
        ['stl', 'starlight'], // STL -> Starlight
        ['starlight', 'stl'], // Starlight -> STL
        // Add more mappings as needed
      ]);

      const normalizedOption = this.normalizeValue(lowerOption);

      const mappedValue = colorMappings.get(normalizedOption);

      if (mappedValue) {
        for (const availableValue of availableValues) {
          const normalizedAvailable = this.normalizeValue(availableValue.toLowerCase());
          const normalizedMapped = this.normalizeValue(mappedValue);

          if (normalizedAvailable === normalizedMapped) {
            return true;
          }
        }
      }
    }

    // Handle communication/connectivity variations
    // Normalize "cellular" → "cell" to match "WIFICELL" with "Wi-Fi + Cellular"
    if (lowerVariance.includes('communication') || lowerVariance.includes('connectivity')) {
      const normalizeComm = (val: string) => this.normalizeValue(val, true).replace('cellular', 'cell');
      const normalizedOption = normalizeComm(optionValue);

      for (const availableValue of availableValues) {
        if (normalizedOption === normalizeComm(availableValue)) {
          return true;
        }
      }
    }

    // Handle model/size variations - strip hyphens for "13-inch" vs "13inch"
    if (lowerVariance.includes('model') || lowerVariance.includes('size') || lowerVariance.includes('screen')) {
      const normalizedOption = this.normalizeValue(optionValue, true);

      for (const availableValue of availableValues) {
        if (normalizedOption === this.normalizeValue(availableValue, true)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if two values are semantically the same (using the same logic as isSemanticMatch)
   */
  public areValuesSemanticallySame(value1: string, value2: string, varianceName: string): boolean {
    // First try normalized comparison
    if (this.normalizeValue(value1) === this.normalizeValue(value2)) {
      return true;
    }

    // Then try semantic mapping
    const lowerVariance = varianceName.toLowerCase();

    if (lowerVariance.includes('color')) {
      const getMappedColorValue = (val: string): string => {
        const normalized = this.normalizeValue(val.toLowerCase());
        const mappings = new Map([
          ['sl', 'slv'], // SL -> SLV (Silver)
          ['slv', 'sl'], // SLV -> SL (reverse mapping)
          ['sb', 'spaceblack'], // SB -> Space Black
          ['spaceblack', 'sb'], // Space Black -> SB
          ['mdn', 'midnight'], // MDN -> Midnight
          ['midnight', 'mdn'], // Midnight -> MDN
          ['stl', 'starlight'], // STL -> Starlight
          ['starlight', 'stl'], // Starlight -> STL
        ]);
        return mappings.get(normalized) || val;
      };

      const mapped1 = getMappedColorValue(value1);
      const mapped2 = getMappedColorValue(value2);

      if (this.normalizeValue(mapped1) === this.normalizeValue(mapped2)) {
        return true;
      }

      // Also check reverse mapping
      if (
        this.normalizeValue(value1) === this.normalizeValue(mapped2) ||
        this.normalizeValue(mapped1) === this.normalizeValue(value2)
      ) {
        return true;
      }
    }

    // Handle communication variations - normalize "cellular" → "cell" to match "WIFICELL" with "Wi-Fi + Cellular"
    if (varianceName.toLowerCase().includes('communication') || varianceName.toLowerCase().includes('connectivity')) {
      const normalizeComm = (val: string) => this.normalizeValue(val, true).replace('cellular', 'cell');
      if (normalizeComm(value1) === normalizeComm(value2)) {
        return true;
      }
    }

    // Handle model/size variations - strip hyphens for "13-inch" vs "13inch"
    if (lowerVariance.includes('model') || lowerVariance.includes('size') || lowerVariance.includes('screen')) {
      if (this.normalizeValue(value1, true) === this.normalizeValue(value2, true)) {
        return true;
      }
    }

    return false;
  }
  isAppleWatchProduct(product: ProductDetailsWithVariant): boolean {
    if (!product?.name) return false;

    const productName = product.name.toLowerCase();

    // Check for Apple Watch indicators in product name
    return (
      productName.includes('apple watch') ||
      productName.includes('watch series') ||
      productName.includes('watch se') ||
      productName.includes('watch ultra')
    );
  }

  /**
   * Enhanced method to find matching products that properly handles Color variance from UI
   */
  findMatchingProductWithColorSupport(
    selectedOptionsMap: Map<string, string>,
    selectedColorValue: string,
    product: ProductDetailsWithVariant,
    newlyChanged: { varianceName: string; value: string },
  ): ProductDetailsWithVariant | undefined {
    // Build complete options map including color
    const completeOptionsMap = new Map(selectedOptionsMap);

    // Add color if it's selected
    if (selectedColorValue) {
      completeOptionsMap.set('Color', selectedColorValue);
    }

    // Add the newly changed option
    completeOptionsMap.set(newlyChanged.varianceName, newlyChanged.value);

    return this.findSmartMatchingProduct(completeOptionsMap, product, newlyChanged);
  }

  /**
   * Get product with caching - now delegates to ProductService unified cache
   */
  getProductWithCache(productId: string): Observable<ProductDetailsWithVariant> {
    return this.productService.getCompleteProductData(productId).pipe(
      map(({ productDetails }) => {
        if (this.isProductWithVariants(productDetails)) {
          return productDetails;
        }
        throw new Error(`Product ${productId} does not have variants`);
      }),
    );
  }
  getAvailableProducts(currentProduct: ProductDetailsWithVariant): ProductDetailsWithVariant[] {
    if (!this.isProductWithVariants(currentProduct)) {
      return [currentProduct];
    }

    const allProducts = [currentProduct, ...(currentProduct.similarProducts || [])];
    const filteredGroupings = this.getGroupingsByPartNumber(currentProduct);
    const allowedProductIds = new Set(filteredGroupings.map((g) => g.parentProductId));

    return allProducts.filter((product) => allowedProductIds.has(product.productId));
  }

  /**
   * Get filtered options for a specific step based on current selections
   */
  getFilteredOptionsForStep(
    stepName: string,
    currentProduct: ProductDetailsWithVariant,
    variantRadioGroups: VariantRadioGroup[],
    selectedColorValue: string,
  ): any[] {
    const availableProducts = this.getAvailableProducts(currentProduct);
    const currentSelections = this.getCurrentSelections(variantRadioGroups, selectedColorValue);

    const selectionsWithoutCurrentStep = { ...currentSelections };
    delete selectionsWithoutCurrentStep[stepName.toLowerCase()];

    const matchingProducts = availableProducts.filter((product) =>
      this.productMatchesSelections(product, selectionsWithoutCurrentStep),
    );

    if (stepName.toLowerCase() === 'color') {
      // Get available color values from matching products
      const availableColorValues = new Set<string>();

      matchingProducts.forEach((product) => {
        if (product.variantGroupings) {
          product.variantGroupings.forEach((grouping) => {
            grouping.options.forEach((option) => {
              const varianceName = option.variance?.name?.toLowerCase();
              if (varianceName === 'color') {
                availableColorValues.add(option.option?.value || '');
              }
            });
          });
        }
      });

      // Return color options that match available values
      // Note: This should return the actual color options with all their properties
      // We'll handle this differently in getFilteredColorOptions
      return Array.from(availableColorValues).map((value) => ({ value }));
    } else {
      const matchingGroup = variantRadioGroups.find((group) => group.name?.toLowerCase() === stepName.toLowerCase());
      return matchingGroup?.options || [];
    }
  }

  /**
   * Check if a product matches the given selections
   */
  productMatchesSelections(product: ProductDetailsWithVariant, selections: Record<string, string>): boolean {
    if (Object.keys(selections).length === 0) {
      return true;
    }

    const productSelections: Record<string, string> = {};

    if (product.variantGroupings) {
      product.variantGroupings.forEach((grouping) => {
        grouping.options.forEach((option) => {
          const varianceName = option.variance?.name?.toLowerCase();
          if (varianceName) {
            productSelections[varianceName] = option.option?.value || '';
          }
        });
      });
    }

    return Object.entries(selections).every(([key, value]) => {
      const productValue = productSelections[key];
      if (!productValue) return false;

      return this.areValuesSemanticallySame(value, productValue, key);
    });
  }

  /**
   * Get current selections from component state
   */
  getCurrentSelections(variantRadioGroups: VariantRadioGroup[], selectedColorValue: string): Record<string, string> {
    const selections: Record<string, string> = {};

    variantRadioGroups.forEach((group) => {
      if (group.selectedValue && group.name) {
        // Find the actual option to get its value instead of using the ID
        const selectedOption = group.options?.find(
          (opt) => opt.id === group.selectedValue || opt.value === group.selectedValue,
        );

        if (selectedOption) {
          selections[group.name.toLowerCase()] = selectedOption.value; // Store VALUE, not ID
        }
      }
    });

    if (selectedColorValue) {
      selections['color'] = selectedColorValue;
    }

    return selections;
  }

  /**
   * Find exact product match based on current selections
   */
  findExactProductMatch(
    currentProduct: ProductDetailsWithVariant,
    variantRadioGroups: VariantRadioGroup[],
    selectedColorValue: string,
  ): ProductDetailsWithVariant | null {
    const currentSelections = this.getCurrentSelections(variantRadioGroups, selectedColorValue);
    const availableProducts = this.getAvailableProducts(currentProduct);

    return (
      availableProducts.find((product) => {
        const matches = this.productMatchesSelections(product, currentSelections);
        return matches;
      }) || null
    );
  }

  /**
   * Get available option values for a specific step
   * Only considers selections from steps that come BEFORE the current step
   */
  getAvailableOptionValues(
    stepName: string,
    currentProduct: ProductDetailsWithVariant,
    variantRadioGroups: VariantRadioGroup[],
    selectedColorValue: string,
    hierarchy?: any,
  ): Set<string> {
    const availableProducts = this.getAvailableProducts(currentProduct);
    const currentSelections = this.getCurrentSelections(variantRadioGroups, selectedColorValue);

    // Only keep selections from steps BEFORE the current step
    const priorSelections: Record<string, string> = {};

    if (hierarchy?.steps) {
      const currentStepIndex = hierarchy.steps.findIndex(
        (step: any) => step.name.toLowerCase() === stepName.toLowerCase(),
      );

      // Only include selections from steps before the current one
      hierarchy.steps.slice(0, currentStepIndex).forEach((step: any) => {
        const stepKey = step.name.toLowerCase();
        if (currentSelections[stepKey]) {
          priorSelections[stepKey] = currentSelections[stepKey];
        }
      });
    } else {
      // Fallback: remove current step only (old behavior)
      Object.assign(priorSelections, currentSelections);
      delete priorSelections[stepName.toLowerCase()];
    }

    const matchingProducts = availableProducts.filter((product) =>
      this.productMatchesSelections(product, priorSelections),
    );

    const availableOptions = new Set<string>();

    matchingProducts.forEach((product) => {
      if (product.variantGroupings) {
        product.variantGroupings.forEach((grouping) => {
          grouping.options.forEach((option) => {
            const varianceName = option.variance?.name?.toLowerCase();
            if (varianceName === stepName.toLowerCase()) {
              availableOptions.add(option.option?.value || '');
            }
          });
        });
      }
    });

    return availableOptions;
  }

  getFilteredVariantGroups(
    currentStep: any,
    variantRadioGroups: VariantRadioGroup[],
    currentProduct: ProductDetailsWithVariant,
    selectedColorValue: string,
  ): Array<
    VariantRadioGroup & {
      availableOptions: RadioOption[];
      unavailableOptions: RadioOption[];
    }
  > {
    if (!currentStep) {
      return [];
    }

    const matchingGroups = variantRadioGroups.filter(
      (group) => group.name?.toLowerCase() === currentStep.name.toLowerCase(),
    );

    return matchingGroups.map((group) => {
      const allOptions = group.options || [];
      const availableOptionValues = this.getAvailableOptionValues(
        group.name || '',
        currentProduct,
        variantRadioGroups,
        selectedColorValue,
      );

      const optionsWithDisabledState = allOptions.map((option) => ({
        ...option,
        disabled: !availableOptionValues.has(option.value),
      }));

      const available = optionsWithDisabledState.filter((opt) => !opt.disabled);
      const unavailable = optionsWithDisabledState.filter((opt) => opt.disabled);

      return {
        ...group,
        availableOptions: available,
        unavailableOptions: unavailable,
        options: optionsWithDisabledState,
      };
    });
  }

  // getFilteredColorOptions(
  //   variantColorOptions: RadioColorOption[],
  //   currentProduct: ProductDetailsWithVariant,
  //   variantRadioGroups: VariantRadioGroup[],
  //   selectedColorValue: string
  // ): (RadioColorOption & { available: boolean })[] {

  //   // Hide color options entirely for Apple Watch products
  //   if (this.isAppleWatchProduct(currentProduct)) {
  //     return [];
  //   }

  //   const availableProducts = this.getAvailableProducts(currentProduct);
  //   const currentSelections = this.getCurrentSelections(variantRadioGroups, selectedColorValue);

  //   // Remove color from current selections to see what colors are available
  //   const selectionsWithoutColor = { ...currentSelections };
  //   delete selectionsWithoutColor['color'];

  //   const matchingProducts = availableProducts.filter(product =>
  //     this.productMatchesSelections(product, selectionsWithoutColor)
  //   );

  //   // Get available color values from matching products
  //   const availableColorValues = new Set<string>();

  //   matchingProducts.forEach(product => {
  //     if (product.variantGroupings) {
  //       product.variantGroupings.forEach(grouping => {
  //         grouping.options.forEach(option => {
  //           const varianceName = option.variance?.name?.toLowerCase();
  //           if (varianceName === 'color') {
  //             availableColorValues.add(option.option?.value || '');
  //           }
  //         });
  //       });
  //     }
  //   });

  //   // Map each color option to include availability
  //   return variantColorOptions.map(option => ({
  //     ...option,
  //     available: availableColorValues.has(option.value)
  //   }));
  // }
  getFilteredColorOptions(
    variantColorOptions: RadioColorOption[],
    currentProduct: ProductDetailsWithVariant,
    variantRadioGroups: VariantRadioGroup[],
    selectedColorValue: string,
  ): (RadioColorOption & { available: boolean })[] {
    // Hide color options entirely for Apple Watch products
    if (this.isAppleWatchProduct(currentProduct)) {
      return [];
    }

    const availableProducts = this.getAvailableProducts(currentProduct);
    const currentSelections = this.getCurrentSelections(variantRadioGroups, selectedColorValue);

    // Remove color from current selections to see what colors are available
    const selectionsWithoutColor = { ...currentSelections };
    delete selectionsWithoutColor['color'];

    const matchingProducts = availableProducts.filter((product) =>
      this.productMatchesSelections(product, selectionsWithoutColor),
    );

    // Get available color values from matching products
    const availableColorValues = new Set<string>();

    matchingProducts.forEach((product) => {
      if (product.variantGroupings) {
        product.variantGroupings.forEach((grouping) => {
          grouping.options.forEach((option) => {
            const varianceName = option.variance?.name?.toLowerCase();
            if (varianceName === 'color') {
              availableColorValues.add(option.option?.value || '');
            }
          });
        });
      }
    });

    // Map each color option to include availability
    return variantColorOptions.map((option) => {
      // Check exact match first
      let available = availableColorValues.has(option.value);
      // If no exact match, try semantic matching
      if (!available) {
        for (const availableValue of availableColorValues) {
          if (this.areValuesSemanticallySame(option.value, availableValue, 'color')) {
            available = true;
            break;
          }
        }
      }

      return {
        ...option,
        available: available,
      };
    });
  }

  resetSubsequentSteps(
    changedVarianceName: string,
    hierarchy: any,
    variantRadioGroups: VariantRadioGroup[],
    selectedColorValue: string,
    completedStepStates: Map<string, RadioOption[]>,
  ): {
    updatedGroups: VariantRadioGroup[];
    updatedColorValue: string;
    updatedStepStates: Map<string, RadioOption[]>;
    newCurrentStep: number;
  } {
    if (!hierarchy || !hierarchy.steps) {
      return {
        updatedGroups: variantRadioGroups,
        updatedColorValue: selectedColorValue,
        updatedStepStates: completedStepStates,
        newCurrentStep: 0,
      };
    }

    const changedStepIndex = hierarchy.steps.findIndex(
      (step) => step.name.toLowerCase() === changedVarianceName.toLowerCase(),
    );

    if (changedStepIndex === -1) {
      return {
        updatedGroups: variantRadioGroups,
        updatedColorValue: selectedColorValue,
        updatedStepStates: completedStepStates,
        newCurrentStep: 0,
      };
    }

    const subsequentStepNames = hierarchy.steps.slice(changedStepIndex + 1).map((step) => step.name.toLowerCase());

    if (subsequentStepNames.length === 0) {
      return {
        updatedGroups: variantRadioGroups,
        updatedColorValue: selectedColorValue,
        updatedStepStates: completedStepStates,
        newCurrentStep: changedStepIndex + 1,
      };
    }

    const hasSubsequentSelections = subsequentStepNames.some((stepName) => {
      if (stepName === 'color') {
        return !!selectedColorValue;
      } else {
        return variantRadioGroups.some((group) => group.name?.toLowerCase() === stepName && group.selectedValue);
      }
    });

    if (!hasSubsequentSelections) {
      return {
        updatedGroups: variantRadioGroups,
        updatedColorValue: selectedColorValue,
        updatedStepStates: completedStepStates,
        newCurrentStep: changedStepIndex + 1,
      };
    }

    const updatedGroups = variantRadioGroups.map((group) => {
      const groupVarianceName = group.name?.toLowerCase() || '';

      if (subsequentStepNames.includes(groupVarianceName)) {
        return {
          ...group,
          selectedValue: '',
        };
      }

      return group;
    });

    let updatedColorValue = selectedColorValue;
    if (subsequentStepNames.includes('color')) {
      updatedColorValue = '';
    }

    const updatedStepStates = new Map(completedStepStates);
    subsequentStepNames.forEach((stepName) => {
      updatedStepStates.delete(stepName);
    });

    return {
      updatedGroups,
      updatedColorValue,
      updatedStepStates,
      newCurrentStep: changedStepIndex + 1,
    };
  }

  isVariantSelectionComplete(hierarchy: any, completedSteps: Set<string>): boolean {
    if (!hierarchy) {
      return true;
    }

    const requiredSteps = hierarchy.steps.filter((step) => step.required);

    if (requiredSteps.length === 0) {
      return true;
    }

    const completedRequiredSteps = requiredSteps.filter((step) => completedSteps.has(step.name.toLowerCase()));

    return completedRequiredSteps.length === requiredSteps.length;
  }

  getCompletedStepsFromState(variantRadioGroups: VariantRadioGroup[], selectedColorValue: string): Set<string> {
    const completed = new Set<string>();

    if (selectedColorValue) {
      completed.add('color');
    }

    variantRadioGroups.forEach((group) => {
      if (group.name && group.selectedValue) {
        completed.add(group.name.toLowerCase());
      }
    });

    return completed;
  }
}
